'use client';
import React, { createContext, useContext, startTransition } from 'react';
import { SessionContext } from '@benzinga/session-context';
import {
  Holding,
  Watchlist,
  WatchlistManager,
  WatchlistManagerEvent,
  WatchlistSymbol,
} from '@benzinga/watchlist-manager';

export interface WatchlistContextType {
  addTickerModal: { show: boolean; ticker: string | null; isLoading: boolean };
  deleteModal: { show: boolean; watchlist: Watchlist | null; isLoading: boolean };
  deleteTickerModal: { show: boolean; ticker: string | null; isLoading: boolean };
  overviewWatchlist: Watchlist | null;
  selectedWatchlist: Watchlist | null;
  setAddTickerModal: (modal: { show: boolean; ticker: string | null; isLoading: boolean }) => void;
  setDeleteModal: (modal: { show: boolean; watchlist: Watchlist | null; isLoading: boolean }) => void;
  setDeleteTickerModal: (modal: { show: boolean; ticker: string | null; isLoading: boolean }) => void;
  setSelectedWatchlist: (watchlist: Watchlist) => void;
  setShowCreateModal: (show: boolean) => void;
  setShowNotificationsModal: (modal: { show: boolean; watchlist: Watchlist | null }) => void;
  setShowYodleeModal: (show: boolean) => void;
  showCreateModal: boolean;
  showNotificationsModal: { show: boolean; watchlist: Watchlist | null };
  showYodleeModal: boolean;
  watchlists: Watchlist[];
}

const WatchlistContext = createContext<WatchlistContextType | undefined>(undefined);

export interface WatchlistProviderProps {
  children: React.ReactNode;
}

const generateOverviewWatchlist = (watchlists: Watchlist[]): Watchlist | null => {
  if (watchlists.length < 2) return null;

  const overviewSymbols = watchlists
    .flatMap(watchlist => watchlist.symbols)
    .reduce((acc: Record<string, WatchlistSymbol>, symbol) => {
      if (acc[symbol.symbol]) {
        if (!symbol.holding) return acc;

        const currentHoldings = acc[symbol.symbol].holding;
        if (!currentHoldings) {
          acc[symbol.symbol].holding = symbol.holding as Holding;
        } else {
          if (Array.isArray(currentHoldings)) {
            acc[symbol.symbol].holding = [...currentHoldings, symbol.holding as Holding];
          } else {
            acc[symbol.symbol].holding = [currentHoldings, symbol.holding as Holding];
          }
        }
      } else {
        acc[symbol.symbol] = symbol;
      }
      return acc;
    }, {});

  return {
    emailSummary: false,
    name: 'Overview',
    realtimeBZ: false,
    realtimePR: false,
    realtimeSEC: false,
    sound: '',
    symbols: Object.values(overviewSymbols),
    watchlistId: 'overview',
  };
};

export const WatchlistProvider: React.FC<WatchlistProviderProps> = ({ children }) => {
  const [selectedWatchlist, setSelectedWatchlistInternal] = React.useState<Watchlist | null>(null);
  const [watchlists, setWatchlists] = React.useState<Watchlist[]>([]);
  const [overviewWatchlist, setOverviewWatchlist] = React.useState<Watchlist | null>(null);

  // Optimize setSelectedWatchlist to prevent unnecessary updates when watchlistId is the same
  const setSelectedWatchlist = React.useCallback((watchlist: Watchlist) => {
    setSelectedWatchlistInternal(prev => {
      // Only update if watchlistId actually changed
      if (prev?.watchlistId === watchlist.watchlistId && prev.symbols.length === watchlist.symbols.length) {
        return prev;
      }
      return watchlist;
    });
  }, []);

  // modals management
  const [showYodleeModal, setShowYodleeModal] = React.useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
  const [showNotificationsModal, setShowNotificationsModal] = React.useState<
    WatchlistContextType['showNotificationsModal']
  >({
    show: false,
    watchlist: null,
  });
  const [deleteModal, setDeleteModal] = React.useState<WatchlistContextType['deleteModal']>({
    isLoading: false,
    show: false,
    watchlist: null,
  });
  // for Overview watchlist
  const [addTickerModal, setAddTickerModal] = React.useState<WatchlistContextType['addTickerModal']>({
    isLoading: false,
    show: false,
    ticker: null,
  });
  const [deleteTickerModal, setDeleteTickerModal] = React.useState<WatchlistContextType['deleteTickerModal']>({
    isLoading: false,
    show: false,
    ticker: null,
  });

  const session = React.useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);

  React.useEffect(() => {
    const init = async () => {
      const watchlists = await watchlistManager.getWatchlists();
      setWatchlists(watchlists?.ok ?? []);

      const overview = generateOverviewWatchlist(watchlists?.ok ?? []);
      setOverviewWatchlist(overview);

      if (watchlists.ok && watchlists.ok.length > 0) {
        setSelectedWatchlistInternal(overview ?? watchlists?.ok?.[0] ?? null);
      } else {
        setShowCreateModal(true);
      }
    };
    startTransition(() => {
      init();
    });
  }, [watchlistManager]);

  // add listener to watchlist manager for watchlist updates
  React.useEffect(() => {
    const subscription = watchlistManager.subscribe((event: WatchlistManagerEvent) => {
      if (event.type === 'watchlist:updated_watchlists') {
        setWatchlists(event.watchlists);
        const overview = generateOverviewWatchlist(event.watchlists);
        setOverviewWatchlist(overview);
        setSelectedWatchlistInternal(
          event.watchlists.find(watchlist => watchlist.watchlistId === selectedWatchlist?.watchlistId) ??
            overview ??
            event.watchlists[0] ??
            null,
        );
        if (showNotificationsModal.show && showNotificationsModal.watchlist !== null) {
          const modalWatchlist = event.watchlists.find(
            watchlist => watchlist.watchlistId === showNotificationsModal.watchlist?.watchlistId,
          );
          if (modalWatchlist) {
            setShowNotificationsModal({ show: true, watchlist: modalWatchlist });
          }
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [selectedWatchlist?.watchlistId, watchlistManager, showNotificationsModal.show, showNotificationsModal.watchlist]);

  const value: WatchlistContextType = {
    addTickerModal,
    deleteModal,
    deleteTickerModal,
    overviewWatchlist,
    selectedWatchlist,
    setAddTickerModal,
    setDeleteModal,
    setDeleteTickerModal,
    setSelectedWatchlist,
    setShowCreateModal,
    setShowNotificationsModal,
    setShowYodleeModal,
    showCreateModal,
    showNotificationsModal,
    showYodleeModal,
    watchlists,
  };

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
};

export const useWatchlistContext = (): WatchlistContextType => {
  const context = useContext(WatchlistContext);
  if (context === undefined) {
    throw new Error('useWatchlistContext must be used within a WatchlistProvider');
  }
  return context;
};
